import { supabase } from './supabaseClient';
import { ACCOUNT_LEVEL_TEAM_ID } from '@/utils/constants.ts';

// ============================================
// TypeScript Interfaces
// ============================================

export interface AccountTeamMember {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'member';
  status: 'active' | 'pending' | 'suspended';
  joinedAt: string;
  lastActive: string;
  avatar?: string;
}

export interface TeamInvitation {
  id: string;
  team_id: string;
  email: string;
  role: 'admin' | 'member' | 'viewer';
  token: string;
  invited_by: string;
  invited_at: string;
  expires_at: string;
  status: 'pending' | 'accepted' | 'expired' | 'cancelled';
}

export interface SubscriptionInfo {
  plan: string;
  maxMembers: number;
  currentMembers: number;
}

// ============================================
// Account Team Management Functions
// ============================================

/**
 * Get subscription info for the user's account
 */
export const getSubscriptionInfo = async (userId: string): Promise<{ data: SubscriptionInfo | null; error: Error | null }> => {
  try {
    // For now, return mock data based on user
    // In production, this would query the actual subscription table
    const mockSubscription: SubscriptionInfo = {
      plan: "Premium",
      maxMembers: 3,
      currentMembers: 1
    };

    return { data: mockSubscription, error: null };
  } catch (error) {
    console.error('Error fetching subscription info:', error);
    return { data: null, error: error as Error };
  }
};

/**
 * Get account team members (people with access to the app)
 */
export const getAccountTeamMembers = async (userId: string): Promise<{ data: AccountTeamMember[] | null; error: Error | null }> => {
  try {
    console.log('🔍 Fetching account team members from database...');

    // Query team_members table and join with auth.users for user details
    const { data: teamMembers, error: membersError } = await supabase
      .from('team_members')
      .select(`
        id,
        user_id,
        role,
        joined_at,
        is_active,
        users!team_members_user_id_fkey(email, full_name)
      `)
      .eq('team_id', ACCOUNT_LEVEL_TEAM_ID)
      .eq('is_active', true);

    if (membersError) {
      console.error('❌ Error fetching team members:', membersError);
      return { data: null, error: membersError };
    }

    console.log('✅ Raw team members data:', teamMembers);

    // Transform the data to match our interface
    const members: AccountTeamMember[] = teamMembers?.map(member => {
      // Handle the users join data properly
      const userData = Array.isArray(member.users) ? member.users[0] : member.users;
      return {
        id: member.user_id,
        email: userData?.email || 'unknown@example.com',
        name: userData?.full_name || 'Unknown User',
        role: member.role as 'admin' | 'member',
        status: 'active' as const,
        joinedAt: member.joined_at,
        lastActive: new Date().toISOString()
      };
    }) || [];

    console.log('✅ Processed team members:', members);
    return { data: members, error: null };
  } catch (error) {
    console.error('Error fetching account team members:', error);
    return { data: null, error: error as Error };
  }
};

/**
 * Remove team member from account access
 */
export const removeAccountTeamMember = async (
  memberId: string,
  currentUserId: string
): Promise<{ error: Error | null }> => {
  try {
    // Prevent self-removal
    if (memberId === currentUserId) {
      throw new Error('You cannot remove yourself from the account');
    }

    // Soft-delete from team_members table
    const { error: removeError } = await supabase
      .from('team_members')
      .update({
        is_active: false,
      })
      .eq('user_id', memberId)
      .eq('team_id', ACCOUNT_LEVEL_TEAM_ID);

    if (removeError) throw removeError;

    return { error: null };

  } catch (error) {
    return {error: error instanceof Error ? error : new Error(String(error))};
  }
};

/**
 * Get pending invitations
 */
export const getPendingInvitations = async (userId: string): Promise<{ data: TeamInvitation[] | null; error: Error | null }> => {
  try {
    console.log('🔍 Fetching pending invitations from database...');

    // Query team_invitations table for pending invitations
    const { data: invitations, error: invitationsError } = await supabase
      .from('team_invitations')
      .select('*')
      .eq('team_id', ACCOUNT_LEVEL_TEAM_ID)
      .eq('status', 'pending')
      .gte('expires_at', new Date().toISOString()); // Only non-expired invitations

    if (invitationsError) {
      console.error('❌ Error fetching pending invitations:', invitationsError);
      return { data: [], error: null };
    }

    console.log('✅ Pending invitations found:', invitations);

    // Transform to match our interface
    const pendingInvitations: TeamInvitation[] = invitations?.map(inv => ({
      id: inv.id,
      team_id: inv.team_id,
      email: inv.email,
      role: inv.role,
      token: inv.token,
      invited_by: inv.invited_by,
      invited_at: inv.invited_at,
      expires_at: inv.expires_at,
      status: inv.status
    })) || [];

    return { data: pendingInvitations, error: null };
  } catch (error) {
    console.error('Error fetching pending invitations:', error);
    return { data: null, error: error as Error };
  }
};

/**
 * Cancel invitation
 */
export const cancelInvitation = async (
  invitationId: string
): Promise<{ error: Error | null }> => {
  try {
    const { error: updateError } = await supabase
      .from('team_invitations')
      .update({
        status: 'cancelled',
      })
      .eq('id', invitationId)
      .eq('status', 'pending');

    if (updateError) {
      return { error: updateError };
    }

    return { error: null };
  } catch (error) {
    console.error('Error cancelling invitation:', error);
    return { error: error as Error };
  }
};

export default {
  getSubscriptionInfo,
  getAccountTeamMembers,
  removeAccountTeamMember,
  getPendingInvitations,
  cancelInvitation
}; 